// Weekly engineer timesheet overview (Session ABU Part C, extended Session AEL D41 + D31). Admin
// only. Two DIFFERENT things called a timesheet appear side by side on this page and they answer
// different questions:
//   - "Time on site (from job records)" is the DERIVED roll-up: what the arrival/departure times
//     captured on every completed visit say, computed live, for any past week.
//   - "Timesheet hours" is the EDITABLE, office-signed-off timesheet: travel, on-site, office and
//     training time, correctable by the office, moving DRAFT -> SUBMITTED -> APPROVED.
// The two can legitimately differ once the office edits an entry. That is the point of an
// editable timesheet, not a bug, so the columns are always labelled distinctly.
//
// api.timesheets.engineers() only ever answers for the CURRENT and PREVIOUS week, so on any other
// week the Timesheet hours / Status columns show "-" and a quiet note explains why, rather than
// showing stale or fabricated status. The row still opens that engineer's detail page for the
// selected week either way.
//
// SESSION AUV, design system Session 8 (People): the status is the registry's `timesheet` domain (the two CHANGE cells:
// DRAFT grey becomes purple, SUBMITTED blue becomes amber, "it waits on the office"; APPROVED stays green), the local
// pill maps and the exported long-date helper are gone (a date prints "5 Oct 2026"), Loader and EmptyState replace the
// bare skeleton and the three message paragraphs, and "This week" is the shared Button. The ListToolbar was already here.
// PAY VISIBILITY IS EXACTLY AS BEFORE (D57 open): this page shows hours and status, never a pay figure. Presentation only.
import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, api, downloadPayrollCsv } from "../../lib/api";
import type { TimesheetEngineersResult, TimesheetStatus, TimesheetWeekResult } from "../../lib/api";
import { formatDurationHm } from "../../lib/timeOnSite";
import { presentableServerMessage } from "../../lib/serverMessage";
import { useAuth } from "../../lib/auth";
import { ConfirmButton } from "../../components/ConfirmButton";
import { initialMobileLayout } from "../../lib/mobileLayout";
import { MobileCardList, MobileListCard } from "../../components/MobileListCard";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/Button";
import { EmptyState } from "../../components/EmptyState";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { ListToolbar } from "../../components/ListToolbar";
import { useListSearch, filterRows, type SearchAccessors } from "../../lib/useListSearch";
import { useSortableColumn, sortRows, type SortAccessors } from "../../lib/useSortableColumn";
import { SortableHeader } from "../../components/SortableHeader";

// The Monday date string N weeks away from the given Monday (YYYY-MM-DD, UTC).
function shiftWeek(weekStart: string, deltaWeeks: number): string {
  const d = new Date(`${weekStart}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + deltaWeeks * 7);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

interface JoinedTimesheetRow {
  engineerId: string;
  engineerName: string;
  jobsAttended: number;
  // Of jobsAttended, how many had BOTH an arrival and a departure recorded, i.e. how many
  // actually contributed to onSiteMinutes below. A visit with no recorded times counts toward
  // jobsAttended but adds nothing to onSiteMinutes, so this column is what discloses that the
  // "Time on site" total is measured over only some of the week's visits, not all of them.
  jobsWithTime: number;
  onSiteMinutes: number;
  timesheetStatus: TimesheetStatus | null;
  timesheetId: string | null;
  timesheetMins: number | null;
}

// True only when the selected week is the current or previous one, i.e. exactly the two weeks
// api.timesheets.engineers() actually knows about. Any other week must never show a status.
export function isRecentTimesheetWeek(data: TimesheetWeekResult | null, engineersData: TimesheetEngineersResult | null): boolean {
  if (!data || !engineersData) return false;
  return data.week.weekStart === engineersData.currentWeekStarting || data.week.weekStart === engineersData.previousWeekStarting;
}

// Joins the derived per-week roll-up (who was on a job, and for how long) with the editable
// timesheet's current/previous-week status, by engineerId. An engineer with a timesheet but no
// job records this week (pure office/travel/training time) still gets a row, at 0 derived minutes,
// so their signed-off hours are never hidden just because no job was attended.
export function joinTimesheetRows(data: TimesheetWeekResult | null, engineersData: TimesheetEngineersResult | null): JoinedTimesheetRow[] {
  if (!data) return [];
  const recent = isRecentTimesheetWeek(data, engineersData);
  const isCurrent = engineersData?.currentWeekStarting === data.week.weekStart;

  const rows = new Map<string, JoinedTimesheetRow>();
  for (const r of data.rows) {
    rows.set(r.engineerId, {
      engineerId: r.engineerId,
      engineerName: r.engineerName,
      jobsAttended: r.jobsAttended,
      jobsWithTime: r.jobsWithTime,
      onSiteMinutes: r.totalMinutes,
      timesheetStatus: null,
      timesheetId: null,
      timesheetMins: null,
    });
  }

  if (recent && engineersData) {
    for (const e of engineersData.engineers) {
      const wk = isCurrent ? e.current : e.previous;
      const existing = rows.get(e.engineerId);
      if (existing) {
        existing.timesheetStatus = wk.status;
        existing.timesheetId = wk.timesheetId;
        existing.timesheetMins = wk.totalMins;
      } else {
        rows.set(e.engineerId, {
          engineerId: e.engineerId,
          engineerName: e.engineerName,
          jobsAttended: 0,
          jobsWithTime: 0,
          onSiteMinutes: 0,
          timesheetStatus: wk.status,
          timesheetId: wk.timesheetId,
          timesheetMins: wk.totalMins,
        });
      }
    }
  }

  return Array.from(rows.values()).sort((a, b) => a.engineerName.localeCompare(b.engineerName));
}

// M-10, Session AQU: search and sort. The week picker stays the page's one list filter and keeps
// its own state; search narrows the joined rows by engineer name, and sort orders them. The week
// totals in the footer stay the WEEK's totals (the figure the page is read for), never a sum of
// the rows a search happens to leave visible.
const TIMESHEET_SEARCH_ACCESSORS: SearchAccessors<JoinedTimesheetRow> = [(r) => r.engineerName];
// Status sorts by workflow stage, not alphabetically, so Draft, Submitted, Approved read in order.
const TIMESHEET_STATUS_RANK: Record<TimesheetStatus, number> = { DRAFT: 0, SUBMITTED: 1, APPROVED: 2 };

// The below-md sort select (a card list has no header to click). Hours and status are blank outside the two recent weeks.
const SORT_OPTIONS = [
  { key: "engineer", label: "Engineer" },
  { key: "onSite", label: "Time on site" },
  { key: "hours", label: "Timesheet hours" },
  { key: "status", label: "Status" },
];

function timesheetSortAccessors(recentWeek: boolean): SortAccessors<JoinedTimesheetRow> {
  return {
    engineer: (r) => r.engineerName,
    jobs: (r) => r.jobsAttended,
    withTime: (r) => r.jobsWithTime,
    onSite: (r) => r.onSiteMinutes,
    // Only the current and previous week show hours or a status; any other week sorts them as
    // blanks, exactly as it renders them, so the order never reveals a figure the page hides.
    hours: (r) => (recentWeek ? r.timesheetMins : null),
    status: (r) => (recentWeek && r.timesheetStatus ? TIMESHEET_STATUS_RANK[r.timesheetStatus] : null),
  };
}

export function TimesheetsPage() {
  const navigate = useNavigate();
  // undefined = the current week (the server resolves it); a YYYY-MM-DD selects a specific week.
  const [week, setWeek] = useState<string | undefined>(undefined);
  const [data, setData] = useState<TimesheetWeekResult | null>(null);
  // Card list below md, table otherwise -- decided once at mount (Session AHB).
  const [mobileCards] = useState(initialMobileLayout);
  const [engineersData, setEngineersData] = useState<TimesheetEngineersResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Whether a retry is worth offering, and something that actually re-runs the load (Session AGV).
  // The error branch used to be a dead end: it rendered no control, Previous/Next are disabled
  // while `currentStart` is undefined after a failed load, and "This week" calls
  // setWeek(undefined) when `week` is ALREADY undefined, which React bails out of -- so no effect
  // re-ran and no request was made. A page whose only escape from a transient blip is a browser
  // refresh is a page that reads as broken.
  const [retryNonce, setRetryNonce] = useState(0);
  const [retryable, setRetryable] = useState(true);
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const load = useCallback((w: string | undefined) => {
    let live = true;
    setLoading(true);
    setError(null);
    Promise.all([
      api.timesheets.week(w),
      // Best-effort: engineers() only covers the current/previous week, and its failure must
      // never block the derived roll-up above from showing. Absence just means every row falls
      // back to the "not available" treatment, never a fabricated status.
      api.timesheets.engineers().catch(() => null),
    ])
      .then(([wk, eng]) => {
        if (!live) return;
        setData(wk);
        setEngineersData(eng);
      })
      .catch((err) => {
        if (!live) return;
        const forbidden = err instanceof ApiError && err.status === 403;
        // A permissions refusal cannot be retried away, so nothing is offered to press for it --
        // the same honesty rule the engineer screen follows.
        setRetryable(!forbidden);
        // Only PROSE is shown. ApiError.code is the HTTP status text or a machine slug whenever the
        // body carried no `message`, so "unauthorized" could otherwise replace a plain-English
        // line with something that reads as a glitch. See lib/serverMessage.ts.
        const prose = err instanceof ApiError ? presentableServerMessage(err.message) : null;
        setError(
          forbidden
            ? "Timesheets are available to administrators only."
            : (prose ?? "Could not load the timesheet. Please try again in a moment."),
        );
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => load(week), [load, week, retryNonce]);

  const { search, setSearch } = useListSearch();
  const sort = useSortableColumn("timesheets");

  const currentStart = data?.week.weekStart;
  const allRows = joinTimesheetRows(data, engineersData);
  const recentWeek = isRecentTimesheetWeek(data, engineersData);
  // The week total is over EVERY engineer's rows, never only the ones a search leaves visible.
  const totalJobsWithTime = allRows.reduce((sum, r) => sum + r.jobsWithTime, 0);
  const rows = sortRows(filterRows(allRows, search, TIMESHEET_SEARCH_ACCESSORS), timesheetSortAccessors(recentWeek), sort);
  // Payroll export is ADMIN only and only useful when the displayed week has at least one APPROVED
  // timesheet -- otherwise the CSV would be empty, so the button is hidden entirely. timesheetStatus
  // is populated only for the current/previous week (recentWeek), so this is naturally false on any
  // older week the roster does not know the status of.
  const canExportPayroll = isAdmin && recentWeek && allRows.some((r) => r.timesheetStatus === "APPROVED");

  async function downloadPayroll() {
    if (!currentStart) return;
    setDownloading(true);
    setActionError(null);
    try {
      await downloadPayrollCsv(currentStart);
    } catch (err) {
      setActionError(
        err instanceof ApiError && err.message ? err.message : "Could not download the payroll CSV. Please try again.",
      );
    } finally {
      setDownloading(false);
    }
  }

  async function approveRow(timesheetId: string, engineerName: string) {
    setBusyIds((s) => new Set(s).add(timesheetId));
    setActionError(null);
    try {
      await api.timesheets.approve(timesheetId);
      load(week);
    } catch (err) {
      setActionError(
        err instanceof ApiError && err.message
          ? err.message
          : `Could not approve ${engineerName}'s timesheet. Please try again.`,
      );
    } finally {
      setBusyIds((s) => {
        const next = new Set(s);
        next.delete(timesheetId);
        return next;
      });
    }
  }

  function goToDetail(engineerId: string) {
    if (!currentStart) return;
    navigate(`/office/timesheets/${encodeURIComponent(engineerId)}/${encodeURIComponent(currentStart)}`);
  }

  return (
    <div>
      <PageHeader
        title="Timesheets"
        className="mb-4"
        subtitle="Time on site per engineer, by week, from the arrival and departure times recorded on each visit, alongside the office-editable timesheet the office signs off."
        actions={
          canExportPayroll && currentStart ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => void downloadPayroll()}
              disabled={downloading}
              icon={<Download aria-hidden size={14} />}
              className="max-sm:w-full"
            >
              {downloading ? "Preparing..." : "Download payroll CSV"}
            </Button>
          ) : undefined
        }
      />

      {/* The week picker is the list's filter, so it sits in the toolbar's filter slot. The
          toolbar renders in every state (loading and error included) because "This week" and
          the week steppers must stay reachable exactly as they were before (Session AGV). */}
      <ListToolbar
        className="mb-4"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search engineer..."
        searchLabel="Search timesheets"
        resultCount={{ shown: rows.length, total: allRows.length, noun: "engineers" }}
        sort={{ state: sort, options: SORT_OPTIONS, onChange: (st) => sort.setSort(st.sortKey, st.sortDir) }}
        onClear={() => setSearch("")}
        filters={
          <>
            <button
              type="button"
              onClick={() => currentStart && setWeek(shiftWeek(currentStart, -1))}
              disabled={!currentStart}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50 max-sm:min-h-11 max-sm:px-4"
            >
              Previous week
            </button>
            <span className="min-w-56 text-center font-heading text-sm text-ele-strong">
              {data ? data.week.label : "..."}
            </span>
            <button
              type="button"
              onClick={() => currentStart && setWeek(shiftWeek(currentStart, 1))}
              disabled={!currentStart}
              className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50 max-sm:min-h-11 max-sm:px-4"
            >
              Next week
            </button>
            <Button variant="secondary" size="sm" onClick={() => setWeek(undefined)}>
              This week
            </Button>
          </>
        }
      />

      {actionError && (
        <p role="alert" className="mb-4 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">
          {actionError}
        </p>
      )}

      {loading ? (
        <Loader label="Loading the timesheets..." rows={6} />
      ) : error ? (
        <div role="alert" className="rounded-ele border border-ele-purple-accent bg-ele-surface p-6 text-center text-ele-strong">
          <p>{error}</p>
          {retryable && (
            <button
              type="button"
              onClick={() => setRetryNonce((n) => n + 1)}
              className="mt-4 inline-flex min-h-11 items-center rounded-ele border border-ele-loler px-4 font-menu text-xs tracking-wide text-ele-loler"
            >
              Try again
            </button>
          )}
        </div>
      ) : !data || allRows.length === 0 ? (
        <EmptyState message="No engineers to show. Create engineer sign-ins on the Engineers page." />
      ) : rows.length === 0 ? (
        <EmptyState role="status" message={`No engineers match "${search.trim()}".`} />
      ) : (
        <>
          {!recentWeek && (
            <p className="mb-3 text-xs text-ele-body opacity-70">
              Timesheet hours and status are shown for the current and previous week only. Open a row to see or edit this
              week's timesheet.
            </p>
          )}
          {mobileCards ? (
            /* Session AHB: below md the table kept Engineer, Timesheet hours and Status, and
               the status pill had 51px of column. The card also brings back the three
               job-record columns, including the "jobs with time" disclosure that keeps the
               time-on-site total from reading as a complete figure when it is not.
               The Approve control stays desktop-only, as it already was: the card taps
               through to the engineer's week, which carries it (the Session ACW decision). */
            <MobileCardList label="Weekly timesheets">
              {rows.map((r) => (
                <MobileListCard
                  key={r.engineerId}
                  to={currentStart ? `/office/timesheets/${encodeURIComponent(r.engineerId)}/${encodeURIComponent(currentStart)}` : undefined}
                  primary={r.engineerName}
                  secondary={`${r.jobsAttended} job${r.jobsAttended === 1 ? "" : "s"} attended | ${formatDurationHm(r.onSiteMinutes)} on site`}
                  meta={`${r.jobsWithTime} of ${r.jobsAttended} with recorded time`}
                  right={
                    <>
                      <span className="font-semibold text-ele-strong">
                        {recentWeek && r.timesheetMins != null ? formatDurationHm(r.timesheetMins) : "-"}
                      </span>
                      {recentWeek && r.timesheetStatus ? <StatusPill domain="timesheet" status={r.timesheetStatus} /> : null}
                    </>
                  }
                />
              ))}
              {/* The table's tfoot week total, kept: it is the figure the week is read for,
                  and dropping it on a phone would be a silent loss rather than a layout fix. */}
              <li className="rounded-ele border border-ele-green bg-ele-inset px-4 py-3">
                <div className="flex items-center justify-between gap-3 font-heading">
                  <span className="text-ele-green">Week total</span>
                  <span className="text-ele-green">{formatDurationHm(data.totalMinutes)}</span>
                </div>
                <div className="mt-0.5 text-xs text-ele-body">
                  {data.totalJobs} job{data.totalJobs === 1 ? "" : "s"} attended | {totalJobsWithTime} with recorded time
                </div>
              </li>
            </MobileCardList>
          ) : (
          <div className="overflow-hidden rounded-ele border border-ele-purple-accent">
            <table className="ele-list-table w-full text-left text-sm" aria-label="Weekly timesheets">
              <colgroup>
                <col style={{ width: "22%" }} />
                <col className="max-md:hidden" style={{ width: "9%" }} />
                <col className="max-md:hidden" style={{ width: "9%" }} />
                <col className="max-md:hidden" style={{ width: "16%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "12%" }} />
                <col className="max-md:hidden" style={{ width: "16%" }} />
                {/* Mobile-only disclosure chevron: the row navigates to that engineer's detail
                    page below md, where Approve (and everything else) is available. */}
                <col className="w-8 md:hidden" />
              </colgroup>
              <thead className="bg-ele-inset font-menu text-xs uppercase tracking-widest text-ele-blue">
                <tr>
                  <SortableHeader label="Engineer" colKey="engineer" sort={sort} onToggle={sort.toggle} />
                  <SortableHeader label="Jobs attended" colKey="jobs" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                  {/* Of the jobs attended, how many had BOTH times recorded: discloses that the
                      Time on site total is measured over only some of the week's visits, not all
                      of them, so it never reads as a complete figure when it is not. */}
                  <SortableHeader label="Jobs with time" colKey="withTime" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                  <SortableHeader label="Time on site (from job records)" colKey="onSite" sort={sort} onToggle={sort.toggle} align="right" className="max-md:hidden" />
                  <SortableHeader label="Timesheet hours" colKey="hours" sort={sort} onToggle={sort.toggle} align="right" />
                  <SortableHeader label="Status" colKey="status" sort={sort} onToggle={sort.toggle} />
                  <th className="cell-actions max-md:hidden px-3 py-2 text-right">Actions</th>
                  <th className="w-8 cell-actions md:hidden" aria-hidden="true" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r.engineerId}
                    className="group cursor-pointer border-t border-ele-purple-accent transition hover:bg-ele-row-hover"
                    onClick={(e) => {
                      if ((e.target as HTMLElement).closest("a,button,input,select,textarea,label")) return;
                      goToDetail(r.engineerId);
                    }}
                  >
                    <td className="px-3 py-2 align-top text-ele-strong">
                      {currentStart ? (
                        <Link
                          to={`/office/timesheets/${encodeURIComponent(r.engineerId)}/${encodeURIComponent(currentStart)}`}
                          className="hover:text-ele-green hover:underline"
                        >
                          {r.engineerName}
                        </Link>
                      ) : (
                        r.engineerName
                      )}
                    </td>
                    <td className="max-md:hidden px-3 py-2 text-right">{r.jobsAttended}</td>
                    <td className="max-md:hidden px-3 py-2 text-right text-ele-body">{r.jobsWithTime}</td>
                    <td className="max-md:hidden px-3 py-2 text-right text-ele-body">{formatDurationHm(r.onSiteMinutes)}</td>
                    <td className="px-3 py-2 text-right text-ele-strong">
                      {recentWeek && r.timesheetMins != null ? formatDurationHm(r.timesheetMins) : "-"}
                    </td>
                    <td className="px-3 py-2 align-top">
                      {recentWeek && r.timesheetStatus ? <StatusPill domain="timesheet" status={r.timesheetStatus} /> : <span className="text-ele-body opacity-60">-</span>}
                    </td>
                    <td className="cell-actions max-md:hidden px-3 py-2 text-right align-top">
                      {recentWeek && r.timesheetStatus === "SUBMITTED" && r.timesheetId ? (
                        <ConfirmButton
                          onConfirm={() => void approveRow(r.timesheetId!, r.engineerName)}
                          busy={busyIds.has(r.timesheetId)}
                          busyLabel="Approving..."
                          label="Approve"
                          confirmLabel={`Confirm: approve ${r.engineerName}'s timesheet for ${data.week.label}?`}
                          className="text-ele-green hover:underline"
                        />
                      ) : (
                        <span className="text-ele-body opacity-40">-</span>
                      )}
                    </td>
                    <td className="cell-actions md:hidden pr-1 text-right">
                      <svg aria-hidden viewBox="0 0 16 16" className="ml-auto h-4 w-4 fill-current text-ele-purple transition-colors group-hover:text-ele-green group-focus-within:text-ele-green">
                        <path d="M6 4l4 4-4 4z" />
                      </svg>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-ele-green bg-ele-inset font-heading">
                  <td className="px-3 py-2 text-ele-green">Week total</td>
                  <td className="max-md:hidden px-3 py-2 text-right text-ele-strong">{data.totalJobs}</td>
                  <td className="max-md:hidden px-3 py-2 text-right text-ele-strong">{totalJobsWithTime}</td>
                  <td className="max-md:hidden px-3 py-2 text-right text-ele-green">{formatDurationHm(data.totalMinutes)}</td>
                  <td className="px-3 py-2"></td>
                  <td className="px-3 py-2"></td>
                  <td className="max-md:hidden px-3 py-2"></td>
                  <td className="md:hidden px-3 py-2"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          )}
        </>
      )}
    </div>
  );
}
